import { Injectable } from '@angular/core';
import { BaseSmartFilterFormViewModel } from '../base-smart-filter-form-view-model';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { TagDomainModel } from '../../../../../domainModels/tag-domain-model';
import { DistinctUtils } from '../../../../../utils/distinct-utils';
import { SelectionUtils } from '../../../../../utils/selection-utils';
import { ProductGroupDomainModel } from '../../../../../domainModels/product-group-domain-model';

@Injectable()
export class AdvancedAddEditSmartFilterFormViewModel extends BaseSmartFilterFormViewModel {
  constructor(
    private productGroupDomainModel: ProductGroupDomainModel,
    private tagDomainModel: TagDomainModel
  ) {
    super();
  }

  private readonly allBadges$ = this.productGroupDomainModel.badges$;
  public readonly groupDetailAppearance$ = window.types.groupDetailAppearance$;
  public readonly groupDetailAromaFlavor$ = window.types.groupDetailAromaFlavor$;
  public readonly groupDetailEffect$ = window.types.groupDetailEffect$;
  public readonly groupDetailEnergyScore$ = window.types.groupDetailEnergyScore$;
  public readonly groupDetailGrowingCondition$ = window.types.groupDetailGrowingCondition$;
  public readonly groupDetailHelpsWith$ = window.types.groupDetailHelpsWith$;
  public readonly craftDesignations$ = window.types.craftDesignations$;
  public readonly gradingProviders$ = window.types.gradingProviders$;
  public readonly productTagGroups$ = this.tagDomainModel.curatedTagGroups$;
  public readonly curatedProductTags$ = this.tagDomainModel.curatedProductTags$;
  public readonly curatedProductTagsWhereSelectionIsId$ = this.tagDomainModel.curatedProductTagsWhereSelectionIsId$;
  public readonly curatedTagGroupDropdownGroupings$ = this.tagDomainModel.curatedTagGroupDropdownGroupings$;

  public readonly hydratedBadgesOnSmartFilter$ = combineLatest([this.smartFilter$, this.allBadges$]).pipe(
    map(([sf, allBadges]) => {
      return allBadges?.filter(b => sf?.badgeIds.indexOf(b?.id) !== -1);
    })
  );

  private readonly _minGradingScore = new BehaviorSubject<number>(null);
  public readonly minGradingScore$ = this._minGradingScore as Observable<number>;
  public connectToMinGradingScore = (minGradingScore: number) => this._minGradingScore.next(minGradingScore);

  private readonly _maxGradingScore = new BehaviorSubject<number>(null);
  public readonly maxGradingScore$ = this._maxGradingScore as Observable<number>;
  public connectToMaxGradingScore = (maxGradingScore: number) => this._maxGradingScore.next(maxGradingScore);

  private readonly _selectedTagGroupIds = new BehaviorSubject<string[]>(null);
  public readonly selectedTagGroupIds$ = this._selectedTagGroupIds.pipe(
    distinctUntilChanged(DistinctUtils.distinctSortedStrings),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  public connectToSelectedTagGroupIds = (selectedTagGroupIds: string[]) => {
    this._selectedTagGroupIds.next(!selectedTagGroupIds?.length ? null : selectedTagGroupIds);
  };

  public readonly selectedTagGroupProductTags$ = combineLatest([
    this.selectedTagGroupIds$,
    this.curatedProductTags$
  ]).pipe(
    map(([selectedTagGroupIds, curatedProductTags$]) => {
      return curatedProductTags$?.filter(productTag => selectedTagGroupIds?.includes(productTag?.groupId));
    }),
    map(productTags => productTags?.map(productTag => SelectionUtils.getProductTagWithStartingLockIcon(productTag))),
    distinctUntilChanged(DistinctUtils.distinctUniquelyIdentifiableArray),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public trackByProvider = (index: number, provider: any): string => {
    return provider?.value;
  };
}
